import { cn } from '../../lib/cn';
import { LegendList } from '../data/LegendList';

export interface ChartLegendItem {
  /** Stable identity — this is what `hidden` and `onToggle` carry. */
  id: string;
  label: string;
  /** A token colour read at runtime, never a literal. */
  color: string;
  /** Optional suffix — a count, a total, a share. Pre-formatted. */
  value?: string;
}

export interface ChartLegendProps {
  /** Names the list. A row of coloured squares has no name of its own. */
  label: string;
  items: ChartLegendItem[];
  /** Ids currently switched off. */
  hidden?: string[];
  /** Omit for a purely descriptive legend — items then render as plain text. */
  onToggle?: (id: string) => void;
  /** `swatch` for filled series, `line` for stroke-only series. */
  shape?: 'swatch' | 'line';
  className?: string;
}

function LegendMark({
  color,
  shape,
  off,
}: {
  color: string;
  shape: 'swatch' | 'line';
  off: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'shrink-0',
        // A line series is a stroke, so the mark is a bare bar; a filled series
        // gets a border so a light token colour still has a 3:1 boundary.
        shape === 'line' ? 'h-[3px] w-5' : 'size-3 border border-line-strong',
        off && 'opacity-40',
      )}
      style={{ backgroundColor: color }}
    />
  );
}

/**
 * The chart legend: a colour swatch plus its label, and — when `onToggle` is
 * supplied — a real toggle backed by caller state.
 *
 * Switched-off items are struck through as well as dimmed, so the state never
 * rests on colour or opacity alone (WCAG 1.4.1).
 */
export function ChartLegend({
  label,
  items,
  hidden = [],
  onToggle,
  shape = 'swatch',
  className,
}: ChartLegendProps) {
  const isOff = (item: ChartLegendItem) => hidden.includes(item.id);

  return (
    <LegendList
      items={items}
      label={label}
      className={cn('flex flex-wrap items-center gap-x-4 gap-y-2', className)}
      getKey={(item) => item.id}
      onItemActivate={onToggle ? (item) => onToggle(item.id) : undefined}
      isPressed={(item) => !isOff(item)}
      actionClassName={(item) =>
        cn(
          // 28px tall and free to wrap: the 44px overlay of `dhd-target` would
          // spill past the row and let the row below steal the tap, so the
          // tight variant keeps the hit area inside the row while still
          // clearing the 24px floor of WCAG 2.5.8 (AA).
          'dhd-target-tight relative inline-flex min-h-7 items-center gap-2 rounded-xs px-1',
          'font-display text-2xs font-semibold uppercase tracking-label',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
          isOff(item) ? 'text-muted line-through' : 'text-body',
        )
      }
      staticClassName="inline-flex items-center gap-2 px-1 font-display text-2xs font-semibold uppercase tracking-label text-body"
      renderItem={(item) => (
        <>
          <LegendMark color={item.color} shape={shape} off={isOff(item)} />
          <span>{item.label}</span>
          {item.value ? <span className="font-body tracking-normal normal-case">{item.value}</span> : null}
        </>
      )}
    />
  );
}
